/**
 * `<caos-environment-connector>` — which two environments are in hand, and which of them is production.
 * (Catalog: Developer surfaces › EnvironmentConnector — pick a source and a target environment, with production unmistakable.)
 *
 * PRODUCTION MUST BE UNMISTAKABLE. The specification says so in those words, and it is the whole
 * reason this is a component rather than two dropdowns. A tool that can point at more than one
 * environment and does not say which one is dangerous; a production chip therefore carries the word
 * "Production" as TEXT, an `aria-label` that says it, and a colour that only repeats what the text
 * already said. Nothing here is distinguished by colour alone, because the one time that fails is
 * the time somebody runs a data load against the wrong environment.
 *
 * A CHIP NAMES THE ENVIRONMENT TWICE — part of its domain, and its identifier. Every sandbox of one
 * organisation has a near-identical name, and "which one is this" is exactly the question that
 * matters right before somebody presses something irreversible.
 *
 * SOURCE AND TARGET ARE ORDERED AND SAID. "From here, to here" is a direction, not a pair, and
 * getting it backwards is the entire class of mistakes this screen exists around. The pair is
 * announced in that order, the swap is a single explicit control, and the component refuses to
 * report a comparison as ready while both ends are the same environment — a comparison of something
 * with itself is not a comparison.
 *
 * Styling seams: `::part(connector)`, `::part(end)`, `::part(chip)`, `::part(chip-name)`,
 * `::part(chip-id)`, `::part(badge)`, `::part(swap)`, `::part(summary)`, `::part(connect)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/** One environment somebody can point at. */
export interface CaosEnvironment {
  /** Stable key — what events carry. */
  readonly key: string;
  /** What it is called. */
  readonly name: string;
  /** Part of its address, so two sandboxes of one organisation can be told apart. */
  readonly domain?: string;
  /** Its own identifier, shown beside the domain for the same reason. */
  readonly identifier?: string;
  /** THIS IS PRODUCTION. Said in words wherever it is drawn, never only in colour. */
  readonly production?: boolean;
  /** The connection is stored and survives a restart. */
  readonly connected?: boolean;
}

const sheet = makeSheet(`
  :host { display: block; }

  .connector { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .ends { display: flex; gap: var(--caos-space-3); align-items: flex-end; flex-wrap: wrap; }

  .actions { display: flex; gap: var(--caos-space-2); }

  .end { display: flex; flex-direction: column; gap: 4px; min-width: 200px; flex: 1 1 200px; }
  .end > .label { font-family: var(--caos-font); font-size: 11.5px; color: var(--caos-color-text-muted); }
  .end select {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 9px;
  }
  .end select:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .chip {
    display: inline-flex;
    align-items: baseline;
    gap: var(--caos-space-2);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-pill);
    background: var(--caos-color-surface-2);
    padding: 4px 11px;
    max-width: 100%;
  }
  /* Production. The colour REPEATS the word inside the chip; it never carries it. */
  .chip[data-production="yes"] {
    border-color: var(--caos-color-danger);
    background: var(--caos-color-danger-bg);
  }

  .chip-name {
    font-family: var(--caos-font);
    font-size: 12.5px;
    font-weight: 600;
    color: var(--caos-color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chip-id {
    font-family: var(--caos-font-mono);
    font-size: 11px;
    color: var(--caos-color-text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .badge {
    font-family: var(--caos-font);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--caos-color-danger-text);
    white-space: nowrap;
  }

  .swap, .connect {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 12px;
    cursor: pointer;
  }
  .swap:disabled, .connect:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }
  .swap:focus-visible, .connect:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .connect:not(:disabled) {
    border-color: var(--caos-color-accent);
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    font-weight: 600;
  }

  .summary { font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-text-muted); }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }
  .summary .warn { color: var(--caos-color-danger-text); font-weight: 600; }
`);

/** How one environment reads on a chip: the domain and the identifier, not just the name. */
export function environmentLabel(env: CaosEnvironment): string {
  const parts = [env.domain, env.identifier].filter((p): p is string => typeof p === 'string' && p !== '');
  return parts.length === 0 ? env.name : `${env.name} — ${parts.join(' · ')}`;
}

export class CaosEnvironmentConnector extends CaosElement {
  static get observedAttributes(): string[] {
    return ['source', 'target', 'busy', 'connect-label'];
  }

  private endsEl: HTMLDivElement;
  private summaryEl: HTMLParagraphElement;
  private connectEl: HTMLButtonElement;
  private swapEl: HTMLButtonElement;

  private data: readonly CaosEnvironment[] = [];

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'connector';
    wrap.setAttribute('part', 'connector');

    this.endsEl = document.createElement('div');
    this.endsEl.className = 'ends';

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.swapEl = document.createElement('button');
    this.swapEl.type = 'button';
    this.swapEl.className = 'swap';
    this.swapEl.setAttribute('part', 'swap');
    this.swapEl.textContent = 'Swap';
    this.swapEl.setAttribute('aria-label', 'Swap the source and the target');
    this.swapEl.addEventListener('click', () => this.swap());

    this.connectEl = document.createElement('button');
    this.connectEl.type = 'button';
    this.connectEl.className = 'connect';
    this.connectEl.setAttribute('part', 'connect');
    this.connectEl.addEventListener('click', () => this.connect());

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.append(this.swapEl, this.connectEl);

    wrap.append(this.endsEl, this.summaryEl, actions);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  get environments(): readonly CaosEnvironment[] {
    return this.data;
  }

  set environments(next: readonly CaosEnvironment[]) {
    this.data = Array.isArray(next) ? next : [];
    this.render();
  }

  get source(): CaosEnvironment | undefined {
    return this.data.find((e) => e.key === this.getAttribute('source'));
  }

  get target(): CaosEnvironment | undefined {
    return this.data.find((e) => e.key === this.getAttribute('target'));
  }

  /** Whether either end is production. Read it to gate anything irreversible. */
  get touchesProduction(): boolean {
    return this.source?.production === true || this.target?.production === true;
  }

  /**
   * Whether the pair describes a real comparison.
   *
   * Both ends RESOLVED and different. Resolved, not merely named: the attributes are commonly set
   * in markup while `environments` arrives later from a fetch, and a key naming nothing — because
   * the list has not landed, or because the key is wrong — is not a chosen environment. Measuring
   * this on the attributes alone reported ready while both ends were still `undefined`, and the
   * connect event would then have carried `{ source: undefined }` to a caller that asked for a
   * comparison it could not run.
   *
   * Different, because a comparison of something with itself is not a comparison, and a screen that
   * offered to run one would be offering to waste somebody's time confidently.
   */
  get ready(): boolean {
    const s = this.source;
    const t = this.target;
    return s !== undefined && t !== undefined && s.key !== t.key;
  }

  /**
   * Why a named end is not resolved, when one is named and nothing answers to it. Said in words
   * rather than falling through to "Choose a source and a target", which would be false while a
   * source is chosen and the list is still on its way.
   */
  private unresolved(): string | null {
    const named = (['source', 'target'] as const).filter((a) => {
      const key = this.getAttribute(a);
      return key !== null && key !== '' && !this.data.some((e) => e.key === key);
    });
    if (named.length === 0) return null;
    if (this.data.length === 0) return 'Waiting for the list of environments';
    return named.length === 2
      ? 'Neither of the chosen environments is in the list'
      : `The chosen ${named[0]} is not in the list`;
  }

  private swap(): void {
    const s = this.getAttribute('source');
    const t = this.getAttribute('target');
    if (s === null && t === null) return;
    if (t === null) this.removeAttribute('source');
    else this.setAttribute('source', t);
    if (s === null) this.removeAttribute('target');
    else this.setAttribute('target', s);
    this.raiseChange();
  }

  private raiseChange(): void {
    this.dispatchEvent(
      new CustomEvent('caos-environment-change', {
        bubbles: true,
        composed: true,
        detail: { source: this.source, target: this.target, touchesProduction: this.touchesProduction },
      }),
    );
  }

  private connect(): void {
    if (!this.ready || this.hasAttribute('busy')) return;
    this.dispatchEvent(
      new CustomEvent('caos-environment-connect', {
        bubbles: true,
        composed: true,
        detail: { source: this.source, target: this.target, touchesProduction: this.touchesProduction },
      }),
    );
  }

  private render(): void {
    this.endsEl.replaceChildren(
      this.end('Source', 'source', this.source),
      this.end('Target', 'target', this.target),
    );

    const nodes: Node[] = [];
    if (!this.ready) {
      const s = this.getAttribute('source');
      const t = this.getAttribute('target');
      const strong = document.createElement('strong');
      strong.textContent =
        s !== null && s !== '' && s === t
          ? 'Source and target are the same environment'
          : (this.unresolved() ?? 'Choose a source and a target');
      nodes.push(strong);
    } else {
      const strong = document.createElement('strong');
      // The direction, in order and in words. Getting it backwards is the whole class of mistake
      // this screen exists around.
      strong.textContent = `From ${environmentLabel(this.source!)} to ${environmentLabel(this.target!)}`;
      nodes.push(strong);
      if (this.touchesProduction) {
        nodes.push(document.createTextNode(' · '));
        const warn = document.createElement('span');
        warn.className = 'warn';
        warn.textContent =
          this.target?.production === true ? 'The target is production' : 'The source is production';
        nodes.push(warn);
      }
    }
    this.summaryEl.replaceChildren(...nodes);

    const busy = this.hasAttribute('busy');
    this.connectEl.textContent = busy ? 'Connecting…' : (this.getAttribute('connect-label') ?? 'Compare');
    this.connectEl.disabled = busy || !this.ready;
    this.swapEl.disabled = busy || (this.getAttribute('source') === null && this.getAttribute('target') === null);
  }

  private end(label: string, attr: 'source' | 'target', chosen: CaosEnvironment | undefined): HTMLElement {
    const box = document.createElement('div');
    box.className = 'end';
    box.setAttribute('part', 'end');

    const text = document.createElement('label');
    text.className = 'label';
    text.textContent = label;

    const select = document.createElement('select');
    select.setAttribute('aria-label', `${label} environment`);
    const blank = document.createElement('option');
    blank.value = '';
    blank.textContent = `Choose a ${label.toLowerCase()}`;
    blank.selected = chosen === undefined;
    select.append(blank);
    for (const env of this.data) {
      const o = document.createElement('option');
      o.value = env.key;
      // The word, in the option too — somebody choosing from a dropdown is choosing before any
      // chip exists to warn them.
      o.textContent = env.production ? `${environmentLabel(env)} — Production` : environmentLabel(env);
      o.selected = env.key === chosen?.key;
      select.append(o);
    }
    select.addEventListener('change', () => {
      if (select.value === '') this.removeAttribute(attr);
      else this.setAttribute(attr, select.value);
      this.raiseChange();
    });
    text.append(select);
    box.append(text);

    if (chosen) box.append(this.chip(chosen));
    return box;
  }

  private chip(env: CaosEnvironment): HTMLElement {
    const chip = document.createElement('span');
    chip.className = 'chip';
    chip.setAttribute('part', 'chip');
    if (env.production) chip.setAttribute('data-production', 'yes');

    const name = document.createElement('span');
    name.className = 'chip-name';
    name.setAttribute('part', 'chip-name');
    name.textContent = env.name;

    chip.append(name);

    const detail = [env.domain, env.identifier].filter((p): p is string => typeof p === 'string' && p !== '');
    if (detail.length > 0) {
      const id = document.createElement('span');
      id.className = 'chip-id';
      id.setAttribute('part', 'chip-id');
      // Every sandbox of one organisation has a near-identical name. "Which one is this" is the
      // question that matters right before somebody presses something irreversible.
      id.textContent = detail.join(' · ');
      chip.append(id);
    }

    if (env.production) {
      const badge = document.createElement('span');
      badge.className = 'badge';
      badge.setAttribute('part', 'badge');
      badge.textContent = 'Production';
      chip.append(badge);
    }

    chip.setAttribute(
      'aria-label',
      `${environmentLabel(env)}${env.production ? ' — Production' : ''}${env.connected ? ' — connected' : ''}`,
    );
    return chip;
  }
}

define('caos-environment-connector', CaosEnvironmentConnector);

declare global {
  interface HTMLElementTagNameMap {
    'caos-environment-connector': CaosEnvironmentConnector;
  }
}
